import * as React from "react"

import { useFrameVariant } from "@/components/ui/frame"
import { cn } from "@/lib/utils"

/**
 * `default` is a plain table for any surface. `card` is the framed table
 * (Clerk-style): the header row sits on the surrounding surface and the body
 * rows form a <FramePanel> of the enclosing Frame's variant (outside a Frame,
 * the app-wide default) — a bordered white card, or flat's borderless muted
 * box — with the outer cells inset to line up with a <FrameHeader>'s title.
 * Inside a <Frame>, put a `card` table straight on the shell — not in a
 * FramePanel.
 */
export type TableVariant = "default" | "card"

export type TableProps = React.ComponentProps<"table"> & {
  variant?: TableVariant
}

/**
 * @example
 * import { Badge } from "@/components/ui/badge"
 * import { Frame, FrameHeader, FrameTitle } from "@/components/ui/frame"
 * import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
 *
 * type Mailbox = { id: string; email: string; sending: boolean }
 *
 * export function Mailboxes({ mailboxes }: { mailboxes: Mailbox[] }) {
 *   return (
 *     <Frame>
 *       <FrameHeader>
 *         <FrameTitle>Mailboxes</FrameTitle>
 *       </FrameHeader>
 *       <Table variant="card">
 *         <TableHeader>
 *           <TableRow>
 *             <TableHead>Address</TableHead>
 *             <TableHead>Status</TableHead>
 *           </TableRow>
 *         </TableHeader>
 *         <TableBody>
 *           {mailboxes.map((m) => (
 *             <TableRow key={m.id}>
 *               <TableCell>{m.email}</TableCell>
 *               <TableCell>
 *                 <Badge variant={m.sending ? "success" : "muted"}>{m.sending ? "Sending" : "Replies only"}</Badge>
 *               </TableCell>
 *             </TableRow>
 *           ))}
 *         </TableBody>
 *       </Table>
 *     </Frame>
 *   )
 * }
 */
function Table({
  className,
  variant = "default",
  ...props
}: TableProps) {
  const frameVariant = useFrameVariant()
  return (
    // card: the scroller clips its rows' shadow, so it gets room below the
    // rows (taken back with a negative margin) and stays unpositioned, so
    // whatever follows the table keeps its clicks in that strip. data-frame
    // picks the panel look; --table-row-bg is that panel's background.
    <div
      className="relative w-full overflow-x-auto data-[variant=card]:static data-[variant=card]:-mb-4 data-[variant=card]:pb-4 data-[frame=default]:[--table-row-bg:var(--background)] data-[frame=flat]:[--table-row-bg:color-mix(in_oklab,var(--muted)_70%,transparent)] dark:data-[frame=flat]:[--table-row-bg:var(--sidebar)] in-dialog:data-[frame=flat]:[--table-row-bg:var(--sidebar)] dark:in-dialog:data-[frame=flat]:[--table-row-bg:var(--background)]"
      data-slot="table-container"
      data-variant={variant}
      data-frame={variant === "card" ? frameVariant : undefined}
    >
      <table
        data-slot="table"
        className={cn(
          "w-full caption-bottom in-data-[variant=card]:border-separate in-data-[variant=card]:border-spacing-0 text-sm",
          className
        )}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn(
        "relative in-data-[variant=card]:rounded-lg in-data-[frame=default]:shadow-xs/5 in-data-[frame=default]:radius-full:shadow-soft before:pointer-events-none before:absolute before:inset-px not-in-data-[frame=default]:before:hidden before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)] [&_tr:last-child]:border-0 in-data-[variant=card]:*:[tr]:border-0 in-data-[variant=card]:*:[tr]:*:[td]:border-b in-data-[variant=card]:*:[tr]:*:[td]:bg-(--table-row-bg) in-data-[variant=card]:*:[tr]:first:*:[td]:first:rounded-ss-lg in-data-[variant=card]:*:[tr]:*:[td]:first:border-s in-data-[variant=card]:*:[tr]:first:*:[td]:border-t in-data-[variant=card]:*:[tr]:last:*:[td]:last:rounded-ee-lg in-data-[variant=card]:*:[tr]:*:[td]:last:border-e in-data-[variant=card]:*:[tr]:first:*:[td]:last:rounded-se-lg in-data-[variant=card]:*:[tr]:last:*:[td]:first:rounded-es-lg in-data-[frame=flat]:*:[tr]:*:[td]:first:border-s-transparent in-data-[frame=flat]:*:[tr]:*:[td]:last:border-e-transparent in-data-[frame=flat]:*:[tr]:first:*:[td]:border-t-transparent in-data-[frame=flat]:*:[tr]:last:*:[td]:border-b-transparent in-data-[variant=card]:*:[tr]:hover:*:[td]:bg-[color-mix(in_srgb,var(--table-row-bg),var(--color-black)_2%)] in-data-[variant=card]:*:[tr]:data-[state=selected]:*:[td]:bg-[color-mix(in_srgb,var(--table-row-bg),var(--color-black)_4%)] dark:in-data-[variant=card]:*:[tr]:data-[state=selected]:*:[td]:bg-[color-mix(in_srgb,var(--table-row-bg),var(--color-white)_4%)] dark:in-data-[variant=card]:*:[tr]:hover:*:[td]:bg-[color-mix(in_srgb,var(--table-row-bg),var(--color-white)_2%)]",
        className
      )}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t in-data-[variant=card]:border-none bg-transparent not-in-data-[variant=card]:bg-[color-mix(in_srgb,var(--card),var(--color-black)_2%)] font-medium dark:not-in-data-[variant=card]:bg-[color-mix(in_srgb,var(--sidebar),var(--color-white)_2%)] [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "relative border-b not-in-data-[variant=card]:hover:bg-[color-mix(in_srgb,var(--background),var(--color-black)_2%)] not-in-data-[variant=card]:data-[state=selected]:bg-[color-mix(in_srgb,var(--background),var(--color-black)_4%)] dark:not-in-data-[variant=card]:data-[state=selected]:bg-[color-mix(in_srgb,var(--background),var(--color-white)_4%)] dark:not-in-data-[variant=card]:hover:bg-[color-mix(in_srgb,var(--background),var(--color-white)_2%)]",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-9 whitespace-nowrap px-2.5 text-left text-xs align-middle font-normal text-muted-foreground leading-none in-data-[variant=card]:first:ps-3.5 in-data-[variant=card]:last:pe-3.5 has-[[role=checkbox]]:w-px last:has-[[role=checkbox]]:ps-0 first:has-[[role=checkbox]]:pe-0",
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "whitespace-nowrap bg-clip-padding in-data-[frame=flat]:bg-clip-border p-2.5 in-data-[slot=table-footer]:py-3.5 align-middle leading-none in-data-[variant=card]:first:ps-[calc(--spacing(3.5)-1px)] in-data-[variant=card]:last:pe-[calc(--spacing(3.5)-1px)] has-[[role=checkbox]]:w-px last:has-[[role=checkbox]]:ps-0 first:has-[[role=checkbox]]:pe-0",
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn(
        "in-data-[variant=card]:my-4 mt-4 text-muted-foreground text-sm",
        className
      )}
      {...props}
    />
  )
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
}
